import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { useBranches } from '@/Pages/Operations/shared';
import { invalidateStations } from '@/Redux/Slices/operationsApi';
import { useAppDispatch } from '@/Redux/store';
import { apiError } from './lib';

interface StationRow {
  id: string;
  branchId: string;
  name: string;
  code: string;
  slaSeconds: number | null;
  sortOrder: number;
  /** Absent on the list endpoint, so only an explicit `false` means inactive. */
  isActive?: boolean;
}

interface Props {
  onChanged: () => void;
}

const BLANK = { branchId: '', name: '', code: '', slaMinutes: '', sortOrder: '0' };

/**
 * §16 — where a kitchen station or a bar is created.
 *
 * The backend has had full station CRUD since P2, and the dashboard only ever
 * read the list to populate a filter on the item form. So an item could be
 * routed to a station, but no station could be made: a new venue had a "send to
 * kitchen" that had nowhere to send to. Same shape as C-14 — the API was
 * complete and the product was unreachable.
 *
 * A station is per branch, because a two-branch venue has two hot lines, and a
 * ticket must print at the one that is cooking it.
 */
export default function StationsTab({ onChanged }: Props) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const { confirm } = useConfirm();
  const branches = useBranches();

  const [rows, setRows] = useState<StationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [branchId, setBranchId] = useState('');
  const [form, setForm] = useState(BLANK);
  const [editing, setEditing] = useState<StationRow | null>(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const dispatch = useAppDispatch();

  const mayEdit = can('kitchen.update');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<StationRow[]>('/kitchen', {
        params: { limit: 200, ...(branchId ? { branchId } : {}) },
      })
      .then(({ data }) => setRows(Array.isArray(data) ? data : []))
      .catch((e) => setError(apiError(e, t('catalog.stations.loadFailed'))))
      .finally(() => setLoading(false));
  }, [branchId, t]);

  useEffect(load, [load]);

  // A station cannot exist without a branch, so default to the only one there
  // is rather than making the operator choose from a list of one.
  const defaultBranch = useMemo(
    () => (branchId || (branches.length === 1 ? branches[0].id : '')),
    [branchId, branches],
  );

  /**
   * Why `POST/PATCH /kitchen/stations` would refuse this form — null when it
   * would take it. `CreateStationDto` marks `branchId`, `name` and `code` all
   * @IsNotEmpty, and `createStation` 404s on a branch that is not this
   * tenant's; `UpdateStationDto` omits `branchId` because a station never moves.
   */
  const saveBlockedBy = !form.name.trim()
    ? t('catalog.stations.nameRequired')
    : !form.code.trim()
      ? t('catalog.stations.codeRequired')
      : !editing && !form.branchId
        ? // With no `branches.view` the picker cannot be populated at all, so
          // say which of the two is missing rather than "pick a branch".
          branches.length
          ? t('catalog.stations.branchRequired')
          : t('catalog.stations.noBranchAvailable')
        : null;

  const openCreate = () => {
    setEditing(null);
    setForm({ ...BLANK, branchId: defaultBranch });
    setOpen(true);
  };

  const openEdit = (row: StationRow) => {
    setEditing(row);
    setForm({
      branchId: row.branchId,
      name: row.name,
      code: row.code,
      // Stored in seconds (§32 analytics); typed in minutes, which is how a
      // kitchen actually talks about a prep target.
      slaMinutes: row.slaSeconds ? String(Math.round(row.slaSeconds / 60)) : '',
      sortOrder: String(row.sortOrder ?? 0),
    });
    setOpen(true);
  };

  const save = async () => {
    if (saveBlockedBy) return;
    setSaving(true);
    try {
      const minutes = Number(form.slaMinutes);
      const body = {
        name: form.name.trim(),
        code: form.code.trim().toUpperCase(),
        slaSeconds:
          form.slaMinutes.trim() && Number.isFinite(minutes) && minutes > 0
            ? Math.round(minutes * 60)
            : undefined,
        sortOrder: Number(form.sortOrder) || 0,
      };
      if (editing) {
        await api.patch(`/kitchen/stations/${editing.id}`, body);
      } else {
        await api.post('/kitchen/stations', { ...body, branchId: form.branchId });
      }
      toast.success(editing ? t('catalog.stations.updated') : t('catalog.stations.created'));
      setOpen(false);
      load();
      // This tab reads /kitchen through axios; the cached station picker on the
      // Kitchen screen only hears about the write through the tag.
      dispatch(invalidateStations());
      onChanged();
    } catch (e) {
      toast.error(apiError(e, t('catalog.stations.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (row: StationRow) => {
    if (!(await confirm({ title: t('catalog.stations.deactivateConfirm', { name: row.name }), tone: 'danger' }))) return;
    try {
      await api.delete(`/kitchen/stations/${row.id}`);
      toast.success(t('catalog.stations.deactivated'));
      load();
      dispatch(invalidateStations());
      onChanged();
    } catch (e) {
      toast.error(apiError(e, t('catalog.stations.saveFailed')));
    }
  };

  const branchName = (id: string) => branches.find((b) => b.id === id)?.name ?? '—';

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="flex flex-wrap items-end justify-between gap-3 p-4">
          {branches.length > 1 && (
            <Field label={t('catalog.stations.branch')}>
              <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                <option value="">{t('catalog.stations.allBranches')}</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {mayEdit && (
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4" />
              {t('catalog.stations.add')}
            </Button>
          )}
        </CardContent>
      </Card>

      {error ? (
        <ErrorState message={error} onRetry={load} />
      ) : loading && !rows.length ? (
        <LoadingState label={t('common.loading')} />
      ) : !rows.length ? (
        <EmptyState
          title={t('catalog.stations.empty')}
          hint={t('catalog.stations.emptyHint')}
          action={
            mayEdit ? (
              <Button size="sm" onClick={openCreate}>
                <Plus className="h-4 w-4" />
                {t('catalog.stations.add')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('catalog.stations.name')}</TH>
                <TH>{t('catalog.stations.code')}</TH>
                <TH>{t('catalog.stations.branch')}</TH>
                <TH className="text-end">{t('catalog.stations.sla')}</TH>
                <TH />
              </TR>
            </THead>
            <tbody>
              {rows.map((row) => (
                <TR key={row.id}>
                  <TD>
                    {row.name}
                    {row.isActive === false && (
                      <Badge tone="neutral" className="ms-2">
                        {t('common.inactive')}
                      </Badge>
                    )}
                  </TD>
                  <TD>
                    <Badge tone="info">{row.code}</Badge>
                  </TD>
                  <TD>{branchName(row.branchId)}</TD>
                  <TD className="text-end tabular-nums">
                    {row.slaSeconds
                      ? t('catalog.stations.slaMinutes', {
                          count: Math.round(row.slaSeconds / 60),
                        })
                      : '—'}
                  </TD>
                  <TD className="text-end">
                    {mayEdit && (
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEdit(row)}
                          title={t('common.edit')}
                          aria-label={t('common.edit')}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {/* `DELETE /kitchen/stations/:id` deactivates rather
                            than deletes — the row cascades to KitchenTicket, so
                            a hard delete would take live tickets with it. The
                            label says deactivate for that reason. */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => void deactivate(row)}
                          title={t('catalog.stations.deactivate')}
                          aria-label={t('catalog.stations.deactivate')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={editing ? t('catalog.stations.edit') : t('catalog.stations.add')}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={saving}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => void save()}
              disabled={saving || !!saveBlockedBy}
              title={saveBlockedBy ?? undefined}
            >
              {t('common.save')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {/* Shown whenever the branch is not already settled — with exactly one
              branch it is filled in for the operator, but with none the picker
              has to appear anyway, or the required field is invisible and the
              form looks complete while the server refuses it. */}
          {!editing && branches.length !== 1 && (
            <Field
              label={t('catalog.stations.branch')}
              required
              error={branches.length ? undefined : t('catalog.stations.noBranchAvailable')}
            >
              <Select
                value={form.branchId}
                onChange={(e) => setForm({ ...form, branchId: e.target.value })}
                disabled={!branches.length}
              >
                <option value="">{t('catalog.stations.chooseBranch')}</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('catalog.stations.name')} required>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t('catalog.stations.namePlaceholder')}
              />
            </Field>
            <Field label={t('catalog.stations.code')} required hint={t('catalog.stations.codeHint')}>
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="HOT"
              />
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('catalog.stations.sla')} hint={t('catalog.stations.slaHint')}>
              <Input
                type="number"
                min={0}
                value={form.slaMinutes}
                onChange={(e) => setForm({ ...form, slaMinutes: e.target.value })}
              />
            </Field>
            <Field label={t('catalog.stations.sortOrder')}>
              <Input
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
              />
            </Field>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
